import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Assessment_Status_Enum as AssessmentStatus,
  InsertAssessmentInput,
  InsertAssessmentMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Assessment_Status_Enum,
  InsertAssessmentDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * tRPC input type derived from GraphQL InsertAssessmentInput.
 * Title is required by the tRPC router (min 1 char), but optional in GraphQL.
 * Relationship arrays are required in GraphQL but optional in tRPC.
 */
type InsertAssessmentTRPCInput = {
  Title: string;
  Status: AssessmentStatus;
  OriginatingItemId?: string | null;
  Summary?: string | null;
  ActualCompletionDate?: string | null;
  NextTestDate?: string | null;
  StartDate?: string | null;
  TargetCompletionDate?: string | null;
  CompletedByUser?: string | null;
  Outcome?: number | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
};

export const useInsertAssessment = createMutationHook({
  name: 'insertAssessment',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.assessment.insert.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: (variables: InsertAssessmentInput): InsertAssessmentTRPCInput => ({
    ...variables,
    Title: variables.Title ?? '',
    Status: variables.Status ?? Assessment_Status_Enum.Notstarted,
  }),
  invalidate: (trpc) => [
    // COI-889: router-level key — the hand-listed set missed `assessmentCount`,
    // the left-nav badge.
    trpc.frontend.assessment.pathKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
  ],
  mapResponse: (result: { Id: string }): InsertAssessmentMutation => ({
    insertAssessmentApi: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertAssessmentDocument,
  graphqlVariables: (variables: InsertAssessmentInput) => ({
    object: variables,
  }),
  evictFields: ['assessment', 'assessment_aggregate'],
  refetchQueries: [
    namedOperations.Query.getAssessmentById,
    namedOperations.Query.getAssessments,
  ],
});
